/**
 * ===================================================
 * Module: frontend.src.types.tenant.ts
 * Description: Tenant, role and group type definitions
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * ===================================================
 */

/** Role a user holds inside their own tenant. Distinct from is_superuser,
 *  which is the Klyra platform administrator flag. */
export type TenantRole = "super_admin" | "admin" | "end_user";

export const TENANT_ROLES: TenantRole[] = ["super_admin", "admin", "end_user"];

/** Lower rank means more privilege. */
const ROLE_RANK: Record<TenantRole, number> = {
  super_admin: 1,
  admin: 2,
  end_user: 3,
};

export const hasRole = (
  role: TenantRole | undefined,
  minimum: TenantRole
): boolean => {
  if (!role) return false;
  return ROLE_RANK[role] <= ROLE_RANK[minimum];
};

export const canAssignRole = (
  actor: TenantRole | undefined,
  target: TenantRole
): boolean => {
  if (actor === "super_admin") return true;
  if (actor === "admin") return target === "end_user";
  return false;
};

/** A page budget: either the organisation pool (super administrators) or one
 *  person's allotment. `available` is what is free to use or hand out. */
export interface PageQuota {
  limit: number | null;
  used: number;
  pending: number;
  allotted: number;
  available: number | null;
  over_allocated: number;
  scope?: "pool" | "allotment";
  can_allot?: boolean;
  source_id?: string | null;
  source_name?: string | null;
}

export interface Tenant {
  id: string;
  name: string;
  my_role: TenantRole | null;
  my_group_ids: string[];
  quota?: PageQuota;
}

export interface TenantGroup {
  id: string;
  name: string;
  description?: string | null;
  is_default: boolean;
  member_count: number;
  project_count: number;
  created_at?: string | null;
}

export interface TenantUser {
  id: string;
  email?: string | null;
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string;
  tenant_role: TenantRole | null;
  account_status?: string;
  group_ids: string[];
  /** Projects this user owns. Drives how strongly removal is warned about. */
  project_count?: number;
  profile_completed?: boolean;
  quota?: PageQuota | null;
  created_at?: string | null;
  last_login?: string | null;
}

export interface TenantUsersResponse {
  users: TenantUser[];
  total: number;
  my_quota?: PageQuota;
}

export interface TenantGroupsResponse {
  groups: TenantGroup[];
  total: number;
}

export interface InviteTenantUserRequest {
  email: string;
  first_name: string;
  last_name: string;
  tenant_role: TenantRole;
  group_ids: string[];
  /** Pages reserved for them out of the inviter's budget. */
  page_limit: number;
}

export interface InviteTenantUserResponse {
  user: TenantUser;
  temporary_password: string;
}

export interface RbacAuditLogEntry {
  id: number;
  action: string;
  event_time?: string | null;
  ip_address?: string | null;
  actor_email?: string | null;
  target_email?: string | null;
  group_name?: string | null;
  details?: Record<string, unknown> | null;
}

export interface RbacAuditLogResponse {
  logs: RbacAuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}
